/**
 * Determines if a color is considered "light" based on YIQ color space calculation.
 * YIQ is used by NTSC color TV system and gives a good approximation of perceived brightness.
 *
 * @param color - The color as hex code (with or without leading #) or standard CSS color name
 * @returns True if the color is perceived as light, false if dark
 */
export const isLightColor = (color: string): boolean => {
  if (!color) return true // Return true for no color (default to white)

  // If it's a hex value with # prefix
  if (color.startsWith('#')) {
    const hex = color.replace('#', '')
    return processHexColor(hex)
  }

  // Check if it's a valid hex code without the # prefix
  const hexRegex = /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/
  if (hexRegex.test(color)) {
    return processHexColor(color)
  }

  // For non-browser environments or safer implementation,
  // we could use a Canvas API or other method to convert
  // Try to convert the color name to a hex value
  try {
    // This approach works in browser environments
    if (typeof document !== 'undefined') {
      const canvas = document.createElement('canvas')
      canvas.width = 1
      canvas.height = 1
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.fillStyle = color
        const isValidColor = ctx.fillStyle !== '#000000'
        if (isValidColor || color.toLowerCase() === 'black') {
          // Extract hex without the #
          const hex = ctx.fillStyle.substring(1)
          return processHexColor(hex)
        }
      }
    }

    // If we couldn't process the color, use white as default
    console.warn(`Could not process color: ${color}, using white as default`)
    return true // White is light
  } catch (error) {
    console.warn(`Error processing color: ${color}, using white as default`, error)
    return true // White is light
  }
}

/**
 * Helper function to process a hex color string and determine if it's light
 *
 * @param hex - Hex color string without the leading '#' (e.g. "FF0000" for red)
 * @returns True if the color is perceived as light, false if dark
 */
const processHexColor = (hex: string): boolean => {
  // Validate hex format - must be either 3 or 6 hex digits
  if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
    // Handle shorthand hex format (e.g. "F00" for red)
    if (/^[0-9A-Fa-f]{3}$/.test(hex)) {
      // Convert 3-digit hex to standard 6-digit format
      // For example: "F00" becomes "FF0000"
      hex = hex.charAt(0) + hex.charAt(0) + hex.charAt(1) + hex.charAt(1) + hex.charAt(2) + hex.charAt(2)
    } else {
      // Invalid hex format, use white as default
      // This happens when the provided string is not a valid hex color
      return true // White is light
    }
  }

  // Parse RGB values from hex string
  const r = parseInt(hex.substring(0, 2), 16) // Red component (0-255)
  const g = parseInt(hex.substring(2, 4), 16) // Green component (0-255)
  const b = parseInt(hex.substring(4, 6), 16) // Blue component (0-255)

  // Calculate brightness using YIQ formula
  // This formula gives more weight to green as human eyes are more sensitive to green
  // Y = 0.299R + 0.587G + 0.114B (from YIQ color space)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000

  // Return true for light colors (YIQ >= 128), false for dark colors
  // The threshold 128 is the midpoint in the 0-255 range
  return yiq >= 128
}

const WHITE_COLOR = 'white'
const WHITE_COLOR_HEX = '#ffffff'

export const isWhiteColor = (color: string): boolean => {
  if (!color) return false
  const normalizedColor = color.toLowerCase().trim()
  if (normalizedColor === WHITE_COLOR) return true
  if (normalizedColor === WHITE_COLOR_HEX) return true
  if (normalizedColor === WHITE_COLOR_HEX.substring(1)) return true
  return false
}
